import React, { useCallback, useState, useContext, useEffect, useRef} from "react";
import { Link } from "react-router-dom";
import AppContext from "../../store/app-context";
import QuoteActions from "./QuoteActions";
import QuoteError from "./QuoteError";
import QuoteExpanded from "./QuoteExpanded";
import styles from "./QuoteItemNewBrand.module.css";
import { ChevronDown, ChevronUp, Lock } from "react-feather";
import { useNavigate } from "react-router-dom";
import useHttp from "../../hooks/use-http";

const QuoteItemNewBrand = (props) => {
  
  const [isExpanded, setIsExpanded] = useState(() => props.isOpen || false);
  const quoteDetailLink = props.status === 'Not Submitted' ? '/'+props.webReference : '/quotes/'+props.webReference;

  const appCtx = useContext(AppContext);
  const [loadstatus, setStatus] = useState("loading"); 
  const [quote, setQuote] = useState();
  const hasFetchedOnce = useRef(false);

  const {sendRequest: getQuoteDetails } = useHttp();

  const navigate = useNavigate();

  const fetchData = useCallback(async (webRef) => {
    setStatus("loading");
    try {
      await getQuoteDetails(
        { url: "/quoteservices/rfqs/rfq/" + webRef + "?pageType=list" },
        (data) => {
          setQuote(data);
          setStatus("success");
        },
        "json"
      );
    } catch (error) {
      setStatus("error");
    }
  }, [getQuoteDetails]);  

  useEffect(() => {
    if (!props.isOpen) {
      setIsExpanded(false);
    }
    else {
      setIsExpanded(true);
      if (!hasFetchedOnce.current) {
        fetchData(props.webReference);
        hasFetchedOnce.current = true;
      }
    } 
  }, [fetchData, props.webReference, props.isOpen]);

  const defaults = {
    date: "Date",
    quote_id: "Quote ID",
    web_ref_id: "Web Ref ID",
    expiration: "Expiration",
    status: "Status",
    lockTitle: "Locked quote",
    lockMsg: "Item and quantity changes are disabled for locked quotes.",
    viewDetails: "View quote details"
  };

  let {
    date,
    quote_id,
    web_ref_id,
    expiration,
    status,
    lockTitle,
    lockMsg,
    viewDetails
  } = defaults;

  if (appCtx?.bioradResources) {
    date = appCtx.bioradResources.Date ?? date;
    quote_id = appCtx.bioradResources.QuoteId ?? quote_id;
    web_ref_id = appCtx.bioradResources.WebRefId ?? web_ref_id;
    expiration = appCtx.bioradResources.Expiration ?? expiration;
    status = appCtx.bioradResources.QuoteStatus ?? status;
    lockTitle = appCtx.bioradResources.LockTitle ?? lockTitle;
    lockMsg = appCtx.bioradResources.LockMsg ?? lockMsg;
    viewDetails = appCtx.bioradResources.ViewDetails ?? viewDetails;
  }

  const toggleExpand = (e) => {
    e.preventDefault();
    setIsExpanded((prev) => {
      const next = !prev;
      if (next && !hasFetchedOnce.current) {
        fetchData(props.webReference);
        hasFetchedOnce.current = true;
      }
      return next;
    });
  };  

  return (
    <div className={styles.row}>
    <div className={styles.rowWrapper}>
      <div className={styles.dropdown} onClick={toggleExpand}>
        <div className={styles.dropdownIcon}>
          <span className={styles.dropdownLabel}>{viewDetails}</span>
          {isExpanded ? <ChevronUp width="12" height="12"/>:
            <ChevronDown width="12" height="12"/>}
        </div>
      </div>
      <div className={styles.linkRow}>
        <span>{date}</span>
        <div className={styles.child}>{props.created ? props.created : ""}</div>
        <span>{quote_id}</span>
        <Link to={quoteDetailLink} className={styles.name}>
          <div className={styles.child}>{props.quoteId ? props.quoteId : ""}</div>
        </Link>
        <div className={styles.lockDiv}>
        {props.locked && (
          <div className={styles.lockWrapper}>
            <div className={styles.lock}>
              <Lock width="16" height="16"/>
            </div>
            <span className={styles.tooltip} role="tooltip" id="lock-tip">
               <span className={styles.toolTipTitle}>{lockTitle}</span>
               {lockMsg}
            </span>
          </div>
        )}
        </div>
        <span>{web_ref_id}</span>
        <Link to={quoteDetailLink} className={styles.webref}>
          <div className={styles.child}>{props.webReference ? props.webReference : ""}</div>
        </Link>
        <span>{expiration}</span>
        <div className={styles.child}>{props.expiryDate ? props.expiryDate : ""}</div>
        <span className={styles.statusLabel}>{status}</span>
        <div className={`${styles.child} ${styles.status} ${styles[props.status?.toLowerCase().replace(/ /g, '')]}`}>{props.status ? props.status : ""}</div>
        
      </div>
      <div  className={styles.actionButton}>
        <QuoteActions status={props.status} 
          quoteId={props.webReference}
          onDeleteSuccess={props.onDeleteSuccess}
          onDeleteError={props.onDeleteError}
        />
      </div>
    </div>
    {isExpanded && (
      <div className={styles.expandedContent}>
        {loadstatus === "loading" && (
          <div className={styles.loaderContainer}>
            <span className={styles.spinner}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30px"
                height="30px"
                viewBox="0 0 128 128"
              >
                <g>
                  <circle cx="16" cy="64" r="16" fill="#03923f" />
                  <circle cx="16" cy="64" r="16" fill="#57b67f" transform="rotate(45,64,64)" />
                  <circle cx="16" cy="64" r="16" fill="#95d1ae" transform="rotate(90,64,64)" />
                  <circle cx="16" cy="64" r="16" fill="#cde9d9" transform="rotate(135,64,64)" />
                  <circle cx="16" cy="64" r="16" fill="#e1f2e8" transform="rotate(180,64,64)" />
                  <circle cx="16" cy="64" r="16" fill="#e1f2e8" transform="rotate(225,64,64)" />
                  <circle cx="16" cy="64" r="16" fill="#e1f2e8" transform="rotate(270,64,64)" />
                  <circle cx="16" cy="64" r="16" fill="#e1f2e8" transform="rotate(315,64,64)" />
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    values="0 64 64;315 64 64;270 64 64;225 64 64;180 64 64;135 64 64;90 64 64;45 64 64"
                    calcMode="discrete"
                    dur="560ms"
                    repeatCount="indefinite"
                  />
                </g>
              </svg>
            </span>
          </div>)}
          {loadstatus === "error" && (<QuoteError onRetry={() => fetchData(props.webReference)} />)}
          {loadstatus === "success" &&
              <QuoteExpanded quote={quote} quoteDetail={false} background={props.background}
              onViewDetails={() => navigate(quoteDetailLink)}/>
            }
      </div>
    )}
    </div>
  );
};

export default QuoteItemNewBrand;
